import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const customerFeedbackService = {
  async create(feedbackData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const snakeData = toSnakeCase({
        ...feedbackData,
        submittedBy: user?.id,
        submittedAt: new Date()?.toISOString()
      });

      const { data, error } = await supabase?.from('customer_feedback')?.insert(snakeData)?.select(`
          *,
          work_order:work_orders(*),
          submitted_by_user:user_profiles!customer_feedback_submitted_by_fkey(id, full_name, email),
          preferred_technician:user_profiles!customer_feedback_preferred_technician_id_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create customer feedback:', error);
      throw error;
    }
  },

  async getByWorkOrderId(workOrderId) {
    try {
      const { data, error } = await supabase?.from('customer_feedback')?.select(`
          *,
          work_order:work_orders(*),
          submitted_by_user:user_profiles!customer_feedback_submitted_by_fkey(id, full_name, email),
          preferred_technician:user_profiles!customer_feedback_preferred_technician_id_fkey(id, full_name, email)
        `)?.eq('work_order_id', workOrderId)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching feedback:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch customer feedback:', error);
      throw error;
    }
  },

  async getAll() {
    try {
      const { data, error } = await supabase?.from('customer_feedback')?.select(`
          *,
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot,
            vehicle:vehicles(marca, modelo, patente)
          ),
          submitted_by_user:user_profiles!customer_feedback_submitted_by_fkey(id, full_name, email),
          preferred_technician:user_profiles!customer_feedback_preferred_technician_id_fkey(id, full_name, email)
        `)?.order('submitted_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching feedback:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch customer feedback:', error);
      throw error;
    }
  },

  async update(feedbackId, updateData) {
    try {
      const snakeData = toSnakeCase(updateData);

      const { data, error } = await supabase?.from('customer_feedback')?.update(snakeData)?.eq('id', feedbackId)?.select(`
          *,
          work_order:work_orders(*),
          submitted_by_user:user_profiles!customer_feedback_submitted_by_fkey(id, full_name, email),
          preferred_technician:user_profiles!customer_feedback_preferred_technician_id_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update customer feedback:', error);
      throw error;
    }
  }
};